import angular from "angular";
import registerBrowserNavigationGuard from "./common/services/browserHistory/browser_navigation_guard";

const module = angular.module("sbApp.AngularBridge", [
    "sbApp.services",
    "sbApp.sbIssueEditorDialog",
    "ui.router",
]);

module.config(function ($urlRouterProvider) {
    "ngInject";
    $urlRouterProvider.deferIntercept();
});

module.run(
    function (
        $sbIssueEditorService,
        $sbDeliverablesNoteStateService,
        $sbExternalService,
        $sbDetailsOverlay,
        $rootScope,
        EVENTS,
        $log,
        $sbTeam,
        $sbProject,
        $state,
        $sbErrorPresenter,
        $sbActivitiesApi,
        $sbAuth,
        $location,
        $urlRouter,
        $window,
        $exceptionHandler
    ) {
        "ngInject";

        const QA_EDITOR_STATE = "sablono.project.qaEditor";
        const VISUALIZATION_EDITOR_STATE =
            "sablono.project.visualizations.editor";
        const PROCESS_TEMPLATE_EDITOR_STATE =
            "sablono.project.processTemplateEditor";

        const browserNavigationGuard = registerBrowserNavigationGuard({
            $rootScope,
            $location,
            $urlRouter,
            $window,
            $exceptionHandler,
            getLeaveGuard: () => {
                if ($sbExternalService?.router()?.mfaEnforcementRequired) {
                    return;
                }

                if (
                    ![
                        QA_EDITOR_STATE,
                        VISUALIZATION_EDITOR_STATE,
                        PROCESS_TEMPLATE_EDITOR_STATE,
                    ].includes($state.current.name)
                ) {
                    return;
                }

                const routeService = $sbExternalService?.router();
                if (routeService?.requestNavigationApproval) {
                    return () => routeService.requestNavigationApproval();
                }
            },
        });
        function isMfaAllowedState(state) {
            return state?.name === "sablono.profile" || state?.data?.forLogin;
        }

        // Builds a $stateChangeStart listener that blocks navigation away from
        // guardedStateName until the Angular-side navigation approval guard (if any) resolves.
        function createLeaveGuardListener(guardedStateName) {
            let shouldSkipLeaveGuard = false;

            // Once an approved retry is in flight, some destination states redirect again via
            // their own resolve (e.g. checklist template migration). Keep skipping until the
            // transition actually settles instead of clearing after a single re-fire, so those
            // chained redirects aren't blocked by this same guard.
            $rootScope.$on("$stateChangeSuccess", () => {
                shouldSkipLeaveGuard = false;
            });
            $rootScope.$on("$stateChangeError", () => {
                shouldSkipLeaveGuard = false;
            });

            return function onStateChangeStart(
                event,
                toState,
                toParams,
                fromState
            ) {
                if (shouldSkipLeaveGuard) {
                    return;
                }

                if ($sbExternalService?.router()?.mfaEnforcementRequired) {
                    return;
                }

                if (
                    fromState?.name !== guardedStateName ||
                    toState?.name === guardedStateName
                ) {
                    return;
                }

                if (toState?.data?.forLogin) {
                    return;
                }

                if (browserNavigationGuard.consumeNavigationApproval()) {
                    shouldSkipLeaveGuard = true;
                    return;
                }

                event.preventDefault();

                const routeService = $sbExternalService?.router();
                if (!routeService?.requestNavigationApproval) {
                    shouldSkipLeaveGuard = true;
                    $state.go(toState.name, toParams);
                    $rootScope.$applyAsync();
                    return;
                }

                routeService
                    .requestNavigationApproval()
                    .then((allowNavigation) => {
                        if (!allowNavigation) {
                            return;
                        }

                        shouldSkipLeaveGuard = true;
                        $state.go(toState.name, toParams);
                        $rootScope.$applyAsync();
                    });
            };
        }

        $rootScope.$on("$stateChangeSuccess", (event, state, params) => {
            const routeService = $sbExternalService?.router();
            if (routeService) {
                routeService.setState(state.name, params);
            }
        });
        // $stateChangeSuccess was not triggering every time to update the router service state.
        // So, we are using $stateChangeSuccess and $rootScope.$on("sb-params-changed") to achieve the same effect.
        // $rootScope.$on("sb-params-changed") is triggered every time we change the params.
        $rootScope.$on("sb-params-changed", () => {
            const routeService = $sbExternalService?.router();
            if (routeService) {
                routeService.setState($state.current.name, $state.params);
            }
        });

        $rootScope.$on("$stateChangeStart", (event, toState) => {
            const routeService = $sbExternalService?.router();
            if (
                !routeService?.mfaEnforcementRequired ||
                isMfaAllowedState(toState)
            ) {
                return;
            }

            event.preventDefault();
            $state.go("sablono.profile");
        });

        $rootScope.$on(
            "$stateChangeStart",
            createLeaveGuardListener(QA_EDITOR_STATE)
        );

        $rootScope.$on(
            "$stateChangeStart",
            createLeaveGuardListener(VISUALIZATION_EDITOR_STATE)
        );

        $rootScope.$on(
            "$stateChangeStart",
            createLeaveGuardListener(PROCESS_TEMPLATE_EDITOR_STATE)
        );

        $sbExternalService
            .activityTrackerState()
            ?.selectDeliverable$.subscribe((deliverable) => {
                void $sbDetailsOverlay.toggleView(
                    "deliverable",
                    deliverable.id
                );
                $rootScope.$apply();
            });

        $sbExternalService
            .activityTrackerState()
            ?.activitySideNavOpening$.subscribe(() => {
                void $sbDetailsOverlay.close();
                $rootScope.$apply();
            });

        $sbExternalService
            ?.router()
            ?.goTo$.subscribe(({ state, params, options }) => {
                $state.go(state || $state.$current.name, params, options);
                $rootScope.$apply();
                $rootScope.$broadcast("sb-params-changed");
            });

        $sbExternalService?.router()?.goToLogin$.subscribe(() => {
            // Angular already owns Cognito sign-out so on the AngularJS side we only need to take care of routing to login page.
            //
            const hasNavigatedToLogin = $sbAuth.goToLoginState(
                $state.current.name,
                $state.params
            );

            // That might be the case when already on the login page
            //
            if (hasNavigatedToLogin) {
                // We still need the legacy logout broadcast so AngularJS services can clear their caches.
                //
                $rootScope.$broadcast("sb.global.logout");
            }
        });

        $sbExternalService
            .showDetailsEventEmitter()
            ?.navigateToDetails$.subscribe(
                ({ viewType, id, context, mode, selectedTab }) => {
                    void $sbDetailsOverlay.toggle(viewType, {
                        id,
                        dataContext: context,
                        mode,
                        selectedTab,
                    });
                    $rootScope.$apply();
                }
            );

        $sbExternalService
            .showDetailsEventEmitter()
            ?.navigateBackFromDetails$.subscribe(() => {
                const backNavigationPromise = $sbDetailsOverlay.goBack();
                if (
                    backNavigationPromise &&
                    typeof backNavigationPromise.catch === "function"
                ) {
                    backNavigationPromise.catch(angular.noop);
                }
                $rootScope.$apply();
            });

        $sbExternalService
            .noteCreationService()
            ?.createNote$.subscribe((context) => {
                $sbTeam
                    .getTeams($sbProject.getCurrentProjectId())
                    .then((teams) => {
                        return Promise.all([
                            Promise.resolve(teams),
                            $sbActivitiesApi.get(context.activityId),
                        ]);
                    })
                    .then(([teams, activity]) => {
                        const defaultTeam = teams.find(
                            (team) => team.id === activity.responsible_team_id
                        );

                        return $sbIssueEditorService.showForActivity(
                            defaultTeam,
                            teams
                        );
                    })
                    .then(function ({ note }) {
                        note.projectId = $sbProject.getCurrentProjectId();
                        $sbExternalService
                            .noteCreationService()
                            ?.noteCreated$.next({
                                context,
                                note,
                            });
                    })
                    .catch(function (error) {
                        // selecting cancel from the dialog throws an undefined
                        if (error) {
                            throw error;
                        }
                    });
            });

        $sbExternalService
            .noteCommentCreationService()
            ?.createNoteComment$.subscribe((context) => {
                /**
                 * createIssueCommentDialog expects a AngularJS note, but
                 * only three properties are needed for adding a comment.
                 */

                const angularJSNote = {
                    ISSUE_ID: context.note.id,
                    ISSUE_TYPE: context.note.type.toUpperCase(),
                    ISSUE_TEXT: context.note.text,
                };

                $sbDeliverablesNoteStateService
                    .createIssueCommentDialog(angularJSNote, context.projectId)
                    .then((createdNoteComment) => {
                        $sbExternalService
                            .noteCommentCreationService()
                            ?.noteCommentCreation$.next({
                                context,
                                result: createdNoteComment,
                            });
                    })
                    .catch((err) => {
                        if (!err) {
                            // dialog cancelled
                            $sbExternalService
                                .noteCommentCreationService()
                                ?.noteCommentCreation$.next({
                                    context,
                                    result: "cancelled",
                                });
                        } else {
                            $sbErrorPresenter.catch(err);
                        }
                    });
            });
    }
);

export default module;
